import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../util/api";
import { useLocation } from "react-router-dom";
import { notify } from "../../util/toast";
import { serverMsgOr } from "../../util/apiFeedback";

import SearchBar from "./SearchBar";
import BidDetailModal from "./BidDetailModal";
import FavoriteStar from "./components/FavoriteStar";
import { favoriteApi } from "./api/favoriteApi";

// MUI
import {
    Box,
    Typography,
    Stack,
    Divider,
    Backdrop,
    CircularProgress,
    Button,
    TextField,
    Dialog,
    DialogContent,
    Checkbox,
    FormControlLabel,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableSortLabel,
    Card,
    CardContent,
    Chip,
} from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import { ds } from "../../styles/designSystem";
import LoadingDots from "../../component/common/LoadingDots";
import ActionButton from "../../component/common/ActionButton";
import ResizableTable from "../../component/common/ResizableTable";
import { formatDateTimeMinute } from "../../util/dateUtils";
import ComboBox from "../../component/common/ComboBox";
import FormField from "../../component/common/FormField";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import SwapVertRoundedIcon from "@mui/icons-material/SwapVertRounded";

// Charts (Recharts)
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip as ReTooltip,
} from "recharts";

const useQuery = () => new URLSearchParams(useLocation().search);

const formatWon = (n) =>
    Number.isFinite(Number(n)) ? `${Number(n).toLocaleString()}원` : "-";
const formatCount = (n) =>
    Number.isFinite(Number(n)) ? `${Number(n).toLocaleString()}건` : "-";
const formatPercent = (n) =>
    Number.isFinite(Number(n))
        ? `${Number(n).toLocaleString("ko-KR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
        : "-";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

/**
 * 낙찰자 칸 문구 — «없음(확정)» 과 «아직 확인 안 됨» 은 다르다.
 *
 * 서버는 실제로 식별된 낙찰자가 있을 때만 winningCompanyName 을 채우고, 없으면 null 과 함께
 * winningStatus 를 보낸다. 예전엔 DB 자리표시자("낙찰업체없음")가 그대로 내려와 두 경우가
 * 한 문구로 뭉개졌다 — 재추적 중인 건까지 «낙찰자가 없다» 고 단정해 보였다.
 */
const winnerLabel = (r) =>
    r?.winningCompanyName || (r?.winningStatus === "NO_WINNER" ? "낙찰자 없음" : "확인 중");

/**
 * 낙찰금액 칸 문구.
 *
 * UNDISCLOSED 는 조달청이 «앞으로도 안 준다» 는 뜻이라 기다릴 필요가 없다.
 * 그냥 비어 있는 것(아직 확인 전)과 구분해야 사용자가 다시 조회하지 않는다.
 */
const winningPriceLabel = (r) => {
    if (r?.winningPrice) return `${Number(r.winningPrice).toLocaleString()}원`;
    if (r?.winningPriceStatus === "UNDISCLOSED") return "비공개";
    if (r?.winningStatus === "NO_WINNER") return "-";
    return "확인 중";
};

/** 실제 값이 아닌 «상태» 문구는 업체명·금액처럼 보이지 않게 흐리게. */
const isPlaceholderText = (text) => text === "확인 중" || text === "비공개" || text === "낙찰자 없음";

/**
 * 서버가 준 실패 요약에서 앞머리의 자바 예외 클래스명을 떼어낸다.
 *
 * 백엔드(CompanySearchService.describe)는 실패 사유를 «예외타입: 메시지» 로 만든다.
 * 클래스명은 사용자에게 아무 의미가 없고, 무엇보다 뒤에 한국어 메시지가 붙으면
 * serverMsgOr 의 «한글이 있으면 사람 대상 문구» 판정을 통과해 그대로 노출된다.
 * 그래서 노출 직전에 접두어만 걷어낸다 — 남은 문구가 여전히 영문 기술 문자열이면
 * serverMsgOr 가 화면 폴백으로 바꿔준다.
 */
const stripExceptionType = (msg) =>
    typeof msg === "string"
        ? msg.replace(/^[A-Za-z_$][\w$.]*(?:Exception|Error|Throwable)\s*:\s*/, "")
        : "";

const cardSx = {
    ...ds.components.external.card,
};

const sectionTitleSx = { ...ds.typography.sectionTitle, fontSize: 18 };

// ✅ 색상 (요청 반영)
const COLORS = {
    winRate: "#22c55e", // 낙찰률(초록)
    winAmount: "#3182f6", // 낙찰금액(파랑)

    // 다양하게
    winCount: "#3b82f6", // 낙찰 건수
    totalBid: "#06b6d4", // 투찰(민트)
    muted: ds.colors.border,
};

// ✅ 커스텀 Tooltip: 위치 튐 방지 + 디자인 정리
/**
 * 필터·정렬 선택지 — <b>패널과 요약 칩이 같은 이름을 쓰게</b> 한곳에 둔다.
 *
 * <p>예전엔 라벨을 선택지 텍스트 안에 넣어("기간: 게시일", "정렬: 개찰일시") 항목 이름을 알렸다.
 * 그러면 드롭다운 폭이 좁을 때 "기간: ...", "정렬: 개찰..." 로 잘려 <b>정작 무슨 기준인지가 먼저
 * 사라진다</b>. 항목 이름은 입력창 위 라벨이 맡고, 여기 label 은 값만 담는다.
 */
const WIN_OPTIONS = [
    { value: "ALL",  label: "전체" },
    { value: "WON",  label: "낙찰" },
    { value: "LOST", label: "미낙찰" },
];

/** 게시·투찰·개찰은 서로 다른 날이다 — 기간을 어느 날짜에 걸지 고르게 한다. */
const DATE_FIELD_OPTIONS = [
    { value: "bidNoticeDate", label: "게시일" },
    { value: "bidDate",       label: "투찰일" },
    { value: "bidOpenDate",   label: "개찰일" },
];

const RANK_OPTIONS = [
    { value: "",    label: "전체" },
    { value: "1",   label: "1위" },
    { value: "2",   label: "2위" },
    { value: "3",   label: "3위" },
    { value: "<=3", label: "3위 이내" },
    { value: "<=5", label: "5위 이내" },
    { value: ">=6", label: "6위 이하" },
];

/** 한 칸에 여러 기준이 묶여 있어(공고명/번호, 게시/투찰/개찰) 헤더 클릭만으로는 다 못 고른다. */
const SORT_OPTIONS = [
    { value: "bidTitle",           label: "공고명" },
    { value: "bidNoticeDate",      label: "게시일시" },
    { value: "bidDate",            label: "투찰일시" },
    { value: "bidOpenDate",        label: "개찰일시" },
    { value: "rank",               label: "순위" },
    { value: "bidPrice",           label: "투찰금액" },
    { value: "winningCompanyName", label: "낙찰업체" },
    { value: "winningPrice",       label: "낙찰금액" },
];

const labelOf = (options, value) =>
    options.find((o) => String(o.value) === String(value ?? ""))?.label || "";

const krw = (n) => (n === "" || n == null ? "" : Number(n).toLocaleString("ko-KR"));

/** "1,000,000 ~ 5,000,000원" · "1,000,000원 이상" — 한쪽만 채운 범위도 말이 되게. */
const rangeText = (min, max) =>
      min && max ? `${krw(min)} ~ ${krw(max)}원`
    : min        ? `${krw(min)}원 이상`
    :              `${krw(max)}원 이하`;

/** 패널 안의 구역 제목 — «필터» 와 «정렬» 을 눈으로 갈라 놓는 유일한 장치라 설명 한 줄을 붙인다. */
function PanelSection({ title, desc, badge }) {
    return (
        <Stack direction="row" alignItems="center" spacing={0.9} sx={{ mb: 1.1 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: ds.colors.textPrimary }}>
                {title}
            </Typography>
            {badge > 0 && (
                <Box sx={{
                    px: 0.7, minWidth: 18, textAlign: "center", borderRadius: 10,
                    bgcolor: ds.colors.primaryBg, color: ds.colors.primary,
                    fontSize: 11, fontWeight: 800, lineHeight: "17px",
                }}>
                    {badge}
                </Box>
            )}
            <Typography sx={{ fontSize: 11.5, color: ds.colors.textMuted }}>{desc}</Typography>
        </Stack>
    );
}

/**
 * 필터 안의 작은 묶음 — «기간», «금액» 처럼 <b>여러 칸이 하나의 조건</b>인 것들을 테두리로 묶는다.
 *
 * <p>기준·시작일·종료일 세 칸이 다른 필터와 같은 줄에 나란히 놓이면 셋이 한 조건이라는 게 안 보인다.
 * 시작일만 넣고 종료일을 비워 둔 채 «왜 이렇게 나오지» 하는 일이 여기서 나온다.
 */
function FieldGroup({ title, hint, children }) {
    return (
        <Box sx={{
            border: `1px solid ${ds.colors.border}`, borderRadius: 1.5,
            p: 1.25, bgcolor: "#fcfdfe",
        }}>
            <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: ds.colors.textSecondary }}>
                    {title}
                </Typography>
                {hint && (
                    <Typography sx={{ fontSize: 11, color: ds.colors.textMuted }}>{hint}</Typography>
                )}
            </Stack>
            {children}
        </Box>
    );
}

/** 최소~최대 한 쌍. 금액 두 칸을 라벨 하나 아래 묶어야 «어느 금액의 범위인지» 가 남는다. */
function RangeInputs({ min, max, onMin, onMax }) {
    return (
        <Stack direction="row" spacing={0.75} alignItems="center">
            <TextField size="small" type="number" placeholder="최소" value={min}
                       onChange={(e) => onMin(e.target.value)} sx={{ flex: 1, minWidth: 0 }} />
            <Typography sx={{ fontSize: 12, color: ds.colors.textMuted }}>~</Typography>
            <TextField size="small" type="number" placeholder="최대" value={max}
                       onChange={(e) => onMax(e.target.value)} sx={{ flex: 1, minWidth: 0 }} />
        </Stack>
    );
}

function CustomTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;

    const p = payload[0];
    return (
        <Box
            sx={{
                bgcolor: ds.colors.surface,
                border: `1px solid ${ds.colors.border}`,
                boxShadow: "0 12px 30px rgba(15, 23, 42, 0.12)",
                borderRadius: 1.5,
                px: 1.25,
                py: 0.75,
            }}
            width={100}
        >
            <Typography sx={{ fontSize: 12, fontWeight: 900, mb: 0.25 }}>
                {p.name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                {p.valueText ?? p.value?.toLocaleString()}
            </Typography>
        </Box>
    );
}

function DonutCard({
                                      title,
                                      subtitle,
                                      mainLabel,
                                      mainValueText,
                                      data,
                                      colors,
                                  }) {
    return (
        <Box
            sx={{
                ...ds.components.external.card,
                p: 2,
                height: 300,            // ✅ 높이 고정 (차트 계산 안정화)
                overflow: "hidden",      // ✅ 밖으로 나가는 것 컷
            }}
        >
            <Stack spacing={0.25} sx={{ mb: 1 }}>
                <Typography sx={ds.typography.sectionTitle}>{title}</Typography>
                <Typography variant="caption" color="text.secondary">
                    {subtitle}
                </Typography>
            </Stack>

            <Box sx={{ position: "relative", height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={data}
                            dataKey="value"
                            nameKey="name"
                            // ✅ 중앙 고정 (튀는 현상 방지)
                            // ✅ 반지름 축소해서 카드 밖으로 안 나가게
                            innerRadius={80}
                            outerRadius={110}
                            startAngle={90}
                            endAngle={-270}
                            paddingAngle={2}
                            isAnimationActive={true} // ✅ 툴팁/레이아웃 튐 줄이기
                        >
                            {data.map((_, idx) => (
                                <Cell key={idx} fill={colors[idx]} />
                            ))}
                        </Pie>

                        <ReTooltip
                            content={<CustomTooltip />}
                            // ✅ 툴팁 튐 방지 옵션
                            wrapperStyle={{ pointerEvents: "none" }}
                            position={{ x: 12, y: 12 }} // ✅ 카드 내부 좌상단 고정(가장 안정적)
                            allowEscapeViewBox={{ x: false, y: false }}
                            isAnimationActive={false}
                        />
                    </PieChart>
                </ResponsiveContainer>

                {/* center label */}
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                        textAlign: "center"
                    }}
                >

                    <Typography variant="caption" color="text.secondary">
                        {mainLabel}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: 16, letterSpacing: "-0.02em" }}>
                        {mainValueText}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

function MiniStat({ title, value, accent }) {
    return (
        <Box
            sx={{
                ...ds.components.external.card,
                p: 1.5,
                textAlign: "left",
            }}
        >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {title}
            </Typography>
            <Typography
                sx={{
                    mt: 0.5,
                    fontWeight: 800,
                    // 금액은 «8,283,286,100원» 처럼 길어져 좁은 칸을 넘친다 — 화면 폭에 따라 줄인다.
                    fontSize: { xs: 15, md: 17 },
                    letterSpacing: "-0.02em",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: accent || "text.primary",
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

const CompanySearchPage = () => {
    const query = useQuery();
    const bizNumber = query.get("bizNumber");

    const [company, setCompany] = useState(null);

    // 즐겨찾기 — 회사가 set 되면 별 상태 조회. 토글은 사업자번호 기준.
    const [companyFav, setCompanyFav] = useState(false);
    useEffect(() => {
        const biz = company?.businessNumber;
        if (!biz) { setCompanyFav(false); return; }
        let cancelled = false;
        (async () => {
            try {
                const favs = await favoriteApi.filterCompanyBiznos([biz]);
                if (!cancelled) setCompanyFav(Array.isArray(favs) ? favs.includes(biz) : !!favs?.[biz]);
            } catch { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, [company?.businessNumber]);
    const toggleCompanyFav = async (next) => {
        const biz = company?.businessNumber;
        if (!biz) return;
        setCompanyFav(next);   // 낙관적
        try {
            if (next) await favoriteApi.addCompany(biz);
            else      await favoriteApi.removeCompany(biz);
        } catch { setCompanyFav(!next); /* 롤백 */ }
    };
    const [stats, setStats] = useState(null);
    const [progress, setProgress] = useState(null);
    // 수집 작업이 «실패로» 끝난 사유. null 이 아니면 진행률 대신 실패 안내 + 재시도를 보여준다.
    const [taskError, setTaskError] = useState(null);
    const [selected, setSelected] = useState(null);


    /**
     * 정렬 — <b>여러 기준을 순서대로</b> 건다. rules[0] 이 1순위.
     *
     * <p>기준 하나로는 «같은 날 여러 건» 의 순서가 들쭉날쭉이다. 앞 기준이 같을 때
     * 다음 기준으로 넘어가게 두면 «게시일 최신순, 같은 날이면 순위 높은 순» 같은 실제 보는 방식이 된다.
     *
     * <p>표 헤더 클릭은 <b>1순위만</b> 바꾼다 — 헤더를 눌렀는데 2순위로 들어가면 아무 일도 안 일어난
     * 것처럼 보인다.
     *
     * <p><b>기본은 게시일시 최신순</b>이다. 개찰일 기준이었을 때는 «공고가 언제 뜬 건인지» 와 목록
     * 순서가 어긋났다 — 오래 전에 올라온 공고라도 개찰이 늦으면 맨 위에 앉는다. 보는 쪽은
     * «최근에 나온 공고» 를 찾는데 개찰 일정은 그 답이 아니다. 공고 목록 화면의 기본 정렬
     * ({@code rgstDt} 내림차순)과도 이제 같은 기준이다.
     */
    const [sortRules, setSortRules] = useState([{ key: "bidNoticeDate", asc: false }]);

    // 헤더의 TableSortLabel 은 «지금 뭘로 정렬 중인가» 하나만 알면 된다 — 1순위를 그대로 준다.
    const sortKey = sortRules[0]?.key ?? "bidNoticeDate";
    const sortAsc = sortRules[0]?.asc ?? false;

    /**
     * 목록 필터 — 전부 «화면에서» 건다.
     *
     * <p>이 목록은 이미 한 업체분 전량이 메모리에 있다(stats.allResults). 서버로 다시 나가면
     * 왕복만 늘고 진행률 스트림과도 어긋난다. 엑셀도 같은 결과를 그대로 쓴다 —
     * «화면과 파일이 다르다» 가 이 화면의 오래된 불만이었다.
     */
    const [fWin, setFWin]         = useState("ALL");      // 낙찰여부
    // 기간을 어느 날짜에 걸까 — 목록 정렬과 같은 «게시일» 이 기본이다.
    // 정렬은 게시일 최신순인데 기간만 개찰일이면, 같은 «최근» 이라는 말이 두 뜻으로 갈린다.
    const [fDateField, setFDateField] = useState("bidNoticeDate");
    const [fFrom, setFFrom]       = useState("");
    const [fTo, setFTo]           = useState("");
    const [fRank, setFRank]       = useState("");         // 순위
    const [fBidMin, setFBidMin]   = useState("");
    const [fBidMax, setFBidMax]   = useState("");
    const [fWinner, setFWinner]   = useState("");         // 낙찰기업명 부분검색
    const [fWinMin, setFWinMin]   = useState("");
    const [fWinMax, setFWinMax]   = useState("");

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (bizNumber) handleSearch(bizNumber);
        // eslint-disable-next-line
    }, [bizNumber]);

    const handleSearch = async (businessNumber) => {
        setLoading(true);
        setStats(null);
        setCompany(null);
        setProgress(null);
        setTaskError(null);

        try {
            // 1) 통계 + 목록
            const res = await api.get(
                `${process.env.REACT_APP_API_BASE_URL}/companies/bidding-results`,
                { bizNumber: businessNumber }
            );

            if (res?.working || res?.status === 202) {
                const token = localStorage.getItem("auth_token");

                const es = new EventSource(
                    `${process.env.REACT_APP_API_BASE_URL}/companies/bidding-results/stream?bizNumber=${encodeURIComponent(
                        businessNumber
                    )}&token=${encodeURIComponent(token || "")}`
                );

                es.addEventListener("progress", (e) => {
                    try {
                        setProgress(JSON.parse(e.data));
                    } catch {
                        // ignore
                    }
                });

                es.addEventListener("done", async () => {
                    es.close();
                    setProgress(null);

                    const statsRes = await api.get(
                        `${process.env.REACT_APP_API_BASE_URL}/companies/bidding-results`,
                        { bizNumber: businessNumber }
                    );

                    /**
                     * ⚠️ 수집이 끝났는데 서버가 또 «작업 중» 이라고 하면 그건 정상이 아니다.
                     *
                     * 서버의 미개찰 판정이 자기가 방금 쓴 값을 다시 «미수집» 으로 보면
                     * done → 재조회 → 202 → (백그라운드에서 같은 작업 재시작) 이 끝없이 돈다.
                     * 실제로 그랬고, 이때 화면은 stats.allResults 가 없어서 목록 자리에
                     * 로딩만 계속 돌았다 — 사용자에겐 «영원히 로딩» 으로만 보이고 이유가 어디에도 없었다.
                     * 여기서 끊고 사유를 남긴다. 이 분기가 다시 타면 서버 회귀 신호다.
                     */
                    if (statsRes?.working || !statsRes?.allResults) {
                        const reason =
                            "수집은 끝났는데 서버가 아직 미완료로 봅니다. 잠시 후 다시 시도해 주세요.";
                        setTaskError(reason);
                        setLoading(false);
                        notify(reason, "warning");
                        return;
                    }

                    setStats(statsRes);
                });

                /**
                 * ⚠️ EventSource 는 서로 다른 두 가지를 «같은 error 핸들러» 로 던진다.
                 *
                 *  1) 서버가 보낸 `event: error` — 워커 스레드가 예외로 죽었다는 통지.
                 *     MessageEvent 로 오므로 e.data 에 JSON({company, message}) 이 들어 있다.
                 *  2) 전송 계층 오류 — 연결 끊김·서버 다운·인증 실패. 그냥 Event 라 e.data 가 없다.
                 *
                 * 구분 기준은 e.data 의 유무 하나뿐이다. 예전 코드는 `es.onerror = () => es.close()`
                 * 로 둘을 뭉뚱그렸고, 그래서 수집 실패가 «스트림만 조용히 닫힘» 으로 끝나
                 * 진행률 바가 영영 멈춘 채 남았다. 이유는 아무 데도 나오지 않았다.
                 * 이 분기를 지우면 그 버그가 그대로 돌아온다.
                 */
                es.addEventListener("error", (e) => {
                    if (!e.data) return; // 전송 계층 오류 → 아래 onerror 의 기존 동작(닫기)에 맡긴다

                    es.close();
                    console.error("❌ 낙찰 결과 수집 실패:", e.data);

                    let payload = null;
                    try {
                        payload = JSON.parse(e.data);
                    } catch {
                        // 문자열로 올 수도 있다 — 그 경우 e.data 자체가 사유다
                    }
                    const reason = serverMsgOr("낙찰 데이터 수집에 실패했습니다. 잠시 후 다시 시도해 주세요.")(
                        stripExceptionType(payload?.message ?? e.data)
                    );

                    // 진행률을 걷어내고(=«수집 중» 표시 해제) 실패 사유를 남긴다.
                    // Backdrop 도 내려야 «다시 시도» 를 누를 수 있다 — 실패는 회사 정보 조회보다
                    // 먼저 도착할 수 있어서 loading 이 true 인 채로 화면이 잠길 수 있다.
                    setProgress(null);
                    setTaskError(reason);
                    setLoading(false);
                    notify(reason, "error");
                });

                es.onerror = () => es.close();
            } else if (res?.allResults) {
                setStats(res);
            } else {
                setStats(null);
            }

            // 2) 회사 정보
            const companyInfo = await api.get(
                `${process.env.REACT_APP_API_BASE_URL}/companies/info`,
                { bizNumber: businessNumber }
            );
            setCompany(companyInfo);

            setLoading(false);
        } catch (err) {
            console.error("❌ 데이터 조회 실패:", err);
            setLoading(false);
            setStats(null);
            setCompany(null);
            setProgress(null);
        }
    };

    const handleSort = (key) => {
        setSortRules((rules) => {
            const [first, ...rest] = rules;
            if (first?.key === key) return [{ key, asc: !first.asc }, ...rest];
            // 같은 기준이 아래 순위에 남아 있으면 뺀다 — 1순위와 겹치면 그 규칙은 영원히 안 쓰인다.
            return [{ key, asc: true }, ...rules.filter((r) => r.key !== key)];
        });
    };

    /** 정렬 규칙 한 줄 편집 — 패널의 «정렬» 구역이 쓴다. */
    const updateSortRule = (i, patch) =>
        setSortRules((rules) => rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    const removeSortRule = (i) =>
        setSortRules((rules) => (rules.length > 1 ? rules.filter((_, idx) => idx !== i) : rules));
    const addSortRule = () =>
        setSortRules((rules) => {
            const used = new Set(rules.map((r) => r.key));
            const next = SORT_OPTIONS.find((o) => !used.has(o.value));
            return next ? [...rules, { key: next.value, asc: false }] : rules;
        });

    /**
     * 정렬 가능한 헤더 칸 — 칸이 열 개라 TableSortLabel 을 열 번 복사하면 오타 하나를 못 찾는다.
     * (컴포넌트가 아니라 렌더 헬퍼다 — 함수 컴포넌트로 만들면 매 렌더마다 remount 된다)
     */
    const sortCell = (key, label, opts = {}) => (
        <TableCell
            align={opts.align}
            sx={{ width: opts.width, minWidth: opts.minWidth, whiteSpace: "nowrap", fontWeight: 900 }}
        >
            <TableSortLabel
                active={sortKey === key}
                direction={sortAsc ? "asc" : "desc"}
                onClick={() => handleSort(key)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    /** 한 칸에 여러 기준이 겹칠 때 쓰는 작은 정렬 링크 (공고명/번호, 게시/투찰/개찰). */
    const miniSort = (key, label) => (
        <TableSortLabel
            active={sortKey === key}
            direction={sortAsc ? "asc" : "desc"}
            onClick={() => handleSort(key)}
            sx={{ fontSize: 11, fontWeight: 600, color: ds.colors.textMuted, whiteSpace: "nowrap" }}
        >
            {label}
        </TableSortLabel>
    );

    /**
     * 검색한 그 업체가 낙찰자인 행인가 — 형광펜 대상.
     *
     * <p>업체명이 아니라 사업자번호로 비교한다. «(주)○○» 과 «주식회사 ○○» 은 같은 회사인데
     * 이름 비교로는 갈리고, 반대로 상호가 같은 다른 법인은 이름 비교로 뭉쳐진다.
     */
    const isSelfWin = (r) =>
        !!bizNumber && !!r?.winningCompanyBizNumber && r.winningCompanyBizNumber === bizNumber;

    /**
     * «우리가 낙찰받은 건» 의 id 집합 — 서버가 준 wonResults 를 그대로 쓴다.
     *
     * <p>화면이 낙찰 여부를 자체 판정하면 위 통계 카드(낙찰 59건)와 목록·요약이 어긋난다.
     * 사업자번호 비교(isSelfWin)는 표의 형광펜용이고, 집계는 이 집합 하나로 통일한다.
     */
    const wonIds = useMemo(
        () => new Set((stats?.wonResults || []).map((r) => r.id)),
        [stats]);

    const displayResults = useMemo(() => {
        const all = stats?.allResults || [];
        const won = stats?.wonResults || [];
        let arr =
            fWin === "WON"  ? [...won]
          : fWin === "LOST" ? all.filter((r) => !wonIds.has(r.id))
          : [...all];

        const num = (v) => (v === "" || v == null || isNaN(Number(v)) ? null : Number(v));
        const bidMin = num(fBidMin), bidMax = num(fBidMax);
        const winMin = num(fWinMin), winMax = num(fWinMax);
        const winnerQ = fWinner.trim().toLowerCase();

        arr = arr.filter((r) => {
            // 기간 — 어느 날짜에 걸지는 사용자가 고른다(게시/투찰/개찰이 다 다른 날이다).
            if (fFrom || fTo) {
                const d = r?.[fDateField];
                if (!d) return false;                 // 값이 없는 행은 기간 조건을 만족한다고 볼 수 없다
                const day = String(d).substring(0, 10);
                if (fFrom && day < fFrom) return false;
                if (fTo && day > fTo) return false;
            }
            if (fRank) {
                const rank = r?.rank;
                if (rank == null) return false;
                if (fRank.startsWith("<=")) { if (rank > Number(fRank.slice(2))) return false; }
                else if (fRank === ">=6")   { if (rank < 6) return false; }
                else if (rank !== Number(fRank)) return false;
            }
            if (bidMin != null && !(r?.bidPrice >= bidMin)) return false;
            if (bidMax != null && !(r?.bidPrice <= bidMax)) return false;
            if (winMin != null && !(r?.winningPrice >= winMin)) return false;
            if (winMax != null && !(r?.winningPrice <= winMax)) return false;
            if (winnerQ && !String(r?.winningCompanyName || "").toLowerCase().includes(winnerQ)) return false;
            return true;
        });

        // 앞 규칙이 «같다» 고 판정하면 다음 규칙으로 넘어간다. 값이 없는 행도 같은 취급이라
        // 자연스럽게 다음 기준이 순서를 잡는다(예전엔 그 자리에서 0 을 반환하고 끝이었다).
        arr.sort((a, b) => {
            for (const rule of sortRules) {
                const av = a?.[rule.key];
                const bv = b?.[rule.key];
                if (av == null || bv == null) continue;

                const diff = (typeof av === "string" && typeof bv === "string")
                    ? (rule.asc ? av.localeCompare(bv) : bv.localeCompare(av))
                    : (rule.asc ? Number(av) - Number(bv) : Number(bv) - Number(av));
                if (diff) return diff;
            }

            // 모든 기준이 «판정 못 함» 으로 끝났을 때만 — 1순위 값이 아예 없는 행을 뒤로 보낸다.
            // 게시일시는 파싱에 실패한 옛 공고가 null 로 남아 있다(그래서 rgstDt IS NULL 백필이 있다).
            // 그 행들이 «최신순» 목록 <b>맨 위</b>에 섞여 앉으면 정렬이 깨진 것으로 보인다.
            // 위 루프의 fall-through 는 그대로 둔다 — 2순위가 있으면 그쪽이 먼저 순서를 잡아야 한다.
            const aFirst = a?.[sortRules[0]?.key];
            const bFirst = b?.[sortRules[0]?.key];
            if (aFirst == null && bFirst != null) return 1;
            if (aFirst != null && bFirst == null) return -1;
            return 0;
        });

        return arr;
    }, [stats, sortRules, fWin, fDateField, fFrom, fTo, fRank,
        fBidMin, fBidMax, fWinner, fWinMin, fWinMax]);

    /**
     * 지금 화면에 남은 것들만의 집계.
     *
     * <p>위 도넛 카드는 «업체 전량» 기준이라 필터를 걸어도 안 움직인다. 그건 그대로 둬야 한다
     * (전체 성적표니까). 대신 «필터를 건 이 구간은 얼마였나» 를 알 방법이 없어서, 목록 바로 위에
     * 필터 적용분 집계를 따로 둔다.
     *
     * <p>낙찰 판정은 서버가 준 wonResults(=wonIds)로 한다 — 도넛 카드와 같은 근거라 숫자가 갈리지 않는다.
     * 월 구분은 <b>개찰일</b> 기준이다. 투찰일로 묶으면 «언제 벌었나» 와 어긋난다(투찰은 개찰 며칠 전).
     */
    const filteredSummary = useMemo(() => {
        const rows = displayResults;
        const wonRows = rows.filter((r) => wonIds.has(r.id));
        const sum = (arr) => arr.reduce((acc, r) => acc + (Number(r.winningPrice) || 0), 0);

        const thisYear = String(new Date().getFullYear());
        // 금액만으로는 «큰 건 하나» 와 «작은 건 여럿» 이 구분되지 않는다 — 건수를 같이 센다.
        const byMonth = new Map();
        wonRows.forEach((r) => {
            const key = r.bidOpenDate ? String(r.bidOpenDate).substring(0, 7) : "미상";
            // 금액·건수만 세지 않고 그 달의 행을 그대로 들고 있는다 —
            // 막대를 눌렀을 때 «무엇을 따서 그 금액이 됐나» 를 바로 보여 주려면 필요하다.
            const cur = byMonth.get(key) || { amount: 0, count: 0, rows: [] };
            cur.amount += Number(r.winningPrice) || 0;
            cur.count += 1;
            cur.rows.push(r);
            byMonth.set(key, cur);
        });

        return {
            count: rows.length,
            wonCount: wonRows.length,
            wonAmount: sum(wonRows),
            thisYearAmount: sum(wonRows.filter(
                (r) => String(r.bidOpenDate || "").startsWith(thisYear))),
            thisYear,
            // 최근 달이 위로 — 목록 기본 정렬(개찰 최신순)과 방향을 맞춘다.
            months: [...byMonth.entries()]
                .map(([month, v]) => ({ month, ...v }))
                .sort((a, b) => b.month.localeCompare(a.month)),
        };
    }, [displayResults, wonIds]);

    /** 월별 막대를 눌렀을 때 그 달의 낙찰 공고를 펼쳐 보는 모달. */
    const [monthDetail, setMonthDetail] = useState(null);

    /** «필터 · 정렬» 팝오버 앵커 — 캘린더 «유형 필터» 와 같은 패턴(버튼 아래로 펼침). */
    const [filterAnchor, setFilterAnchor] = useState(null);

    /**
     * 버튼에 뱃지로 띄울 «켜진 필터 개수».
     *
     * <p>패널을 접어 두면 무엇이 걸려 있는지 안 보인다 — 숫자가 없으면 «왜 12건만 나오지» 를
     * 패널을 열어 봐야 안다. 그룹 단위로 센다(기간 두 칸은 1개, 금액 두 칸도 1개).
     */
    const activeFilterCount =
        (fWin !== "ALL" ? 1 : 0) + (fFrom || fTo ? 1 : 0) + (fRank ? 1 : 0)
        + (fBidMin || fBidMax ? 1 : 0) + (fWinMin || fWinMax ? 1 : 0) + (fWinner ? 1 : 0);

    /** 필터가 하나라도 걸려 있나 — «초기화» 버튼을 언제 살릴지, 건수를 어떻게 표기할지 정한다. */
    const filterActive = fWin !== "ALL" || fFrom || fTo || fRank
        || fBidMin || fBidMax || fWinner || fWinMin || fWinMax;

    /**
     * 지금 걸려 있는 조건을 한 줄 칩으로.
     *
     * <p>패널을 접어 두면 «왜 12건만 나오지» 를 패널을 열어 봐야 알 수 있었다. 버튼의 숫자 뱃지는
     * 개수만 알려 줄 뿐 <b>무슨 조건인지</b>는 말해 주지 않는다. 칩 하나가 항목 이름과 기준값을
     * 같이 들고 있고, x 로 그 조건만 떼어 낼 수 있다.
     */
    const filterChips = useMemo(() => {
        const chips = [];
        if (fWin !== "ALL")
            chips.push({ key: "win", label: `낙찰 여부 · ${labelOf(WIN_OPTIONS, fWin)}`,
                         clear: () => setFWin("ALL") });
        if (fFrom || fTo) {
            const field = labelOf(DATE_FIELD_OPTIONS, fDateField);
            const range = fFrom && fTo ? `${fFrom} ~ ${fTo}` : fFrom ? `${fFrom}부터` : `${fTo}까지`;
            chips.push({ key: "date", label: `${field} · ${range}`,
                         clear: () => { setFFrom(""); setFTo(""); } });
        }
        if (fRank)
            chips.push({ key: "rank", label: `우리 순위 · ${labelOf(RANK_OPTIONS, fRank)}`,
                         clear: () => setFRank("") });
        if (fBidMin || fBidMax)
            chips.push({ key: "bid", label: `투찰금액 · ${rangeText(fBidMin, fBidMax)}`,
                         clear: () => { setFBidMin(""); setFBidMax(""); } });
        if (fWinMin || fWinMax)
            chips.push({ key: "winPrice", label: `낙찰금액 · ${rangeText(fWinMin, fWinMax)}`,
                         clear: () => { setFWinMin(""); setFWinMax(""); } });
        if (fWinner)
            chips.push({ key: "winner", label: `낙찰기업명 · ${fWinner}`,
                         clear: () => setFWinner("") });
        return chips;
    }, [fWin, fDateField, fFrom, fTo, fRank, fBidMin, fBidMax, fWinMin, fWinMax, fWinner]);

    /** "게시일시 ↓ › 순위 ↑" — 규칙이 여러 개일 때 순서가 한눈에 보여야 한다. */
    const sortSummary = sortRules
        .map((r) => `${labelOf(SORT_OPTIONS, r.key)} ${r.asc ? "↑" : "↓"}`)
        .join(" › ");

    const resetFilters = () => {
        setFWin("ALL"); setFDateField("bidNoticeDate"); setFFrom(""); setFTo("");
        setFRank(""); setFBidMin(""); setFBidMax(""); setFWinner(""); setFWinMin(""); setFWinMax("");
    };

    /**
     * 엑셀 — <b>화면에 보이는 그대로</b>(필터·정렬 적용) 내려받는다.
     *
     * <p>예전엔 서버 export 엔드포인트가 필터와 무관하게 전량을 내려줘서, 화면에서 12건으로 좁혀
     * 놓고 받으면 파일엔 700건이 들어 있었다. 목록이 이미 메모리에 다 있으므로 여기서 만든다 —
     * 서버 왕복도 없고 «화면 = 파일» 이 보장된다.
     */
    const handleDownload = async () => {
        if (!displayResults.length) {
            notify("내려받을 결과가 없습니다.", "warning");
            return;
        }
        setLoading(true);
        try {
            const ExcelJS = (await import("exceljs")).default ?? (await import("exceljs"));
            const wb = new ExcelJS.Workbook();
            const ws = wb.addWorksheet("투찰결과");

            ws.columns = [
                { header: "No",       key: "no",      width: 6 },
                { header: "공고명",    key: "title",   width: 52 },
                { header: "공고번호",  key: "no2",     width: 20 },
                { header: "게시일시",  key: "posted",  width: 18 },
                { header: "투찰일시",  key: "bidAt",   width: 18 },
                { header: "개찰일시",  key: "openAt",  width: 18 },
                { header: "순위",     key: "rank",    width: 8 },
                { header: "투찰금액",  key: "bidPrc",  width: 16 },
                { header: "낙찰업체",  key: "winner",  width: 26 },
                { header: "낙찰금액",  key: "winPrc",  width: 16 },
            ];
            ws.getRow(1).font = { bold: true };
            ws.views = [{ state: "frozen", ySplit: 1 }];

            displayResults.forEach((r, i) => {
                const row = ws.addRow({
                    no: i + 1,
                    title: r.bidTitle || "",
                    no2: `${r.bidNoticeNumber || ""}${r.bidNoticeOrder ? `-${r.bidNoticeOrder}` : ""}`,
                    posted: formatDateTimeMinute(r.bidNoticeDate, ""),
                    bidAt: formatDateTimeMinute(r.bidDate, ""),
                    openAt: formatDateTimeMinute(r.bidOpenDate, ""),
                    rank: r.rank ?? "",
                    bidPrc: r.bidPrice ?? "",
                    winner: winnerLabel(r),
                    winPrc: r.winningPrice ?? "",
                });
                row.getCell("bidPrc").numFmt = "#,##0";
                row.getCell("winPrc").numFmt = "#,##0";
                // 화면의 형광펜과 같은 표시 — 파일만 보고도 «우리가 딴 건» 을 찾을 수 있어야 한다.
                if (isSelfWin(r)) {
                    row.getCell("winner").fill = {
                        type: "pattern", pattern: "solid", fgColor: { argb: "FFFDE68A" },
                    };
                    row.getCell("winner").font = { bold: true };
                }
            });

            const buf = await wb.xlsx.writeBuffer();
            const blobObj = new Blob([buf], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blobObj);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `bidding-results-${bizNumber}-${displayResults.length}건.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("엑셀 다운로드 실패:", error);
            notify("엑셀 생성에 실패했습니다.", "error");
        } finally {
            setLoading(false);
        }
    };

    // -------------------------
    // ✅ CHART DATA (요구 반영)
    // -------------------------
    const totalBidCount = Number(stats?.totalBidCount || 0);
    const totalWinCount = Number(stats?.totalWinCount || 0);
    const lostCount = Math.max(0, totalBidCount - totalWinCount);

    const totalBidAmount = Number(stats?.totalBidAmount || 0);
    const totalWinAmount = Number(stats?.totalWinAmount || 0);
    const lostAmount = Math.max(0, totalBidAmount - totalWinAmount);

    const winRate = clamp(Number(stats?.winRate || 0), 0, 100);

    // 1) 투찰/낙찰(건수) -> 낙찰 vs 미낙찰 (도넛)
    const bidWinCountDonut = useMemo(
        () => [
            { name: "낙찰", value: totalWinCount },
            { name: "미낙찰", value: lostCount },
        ],
        [totalWinCount, lostCount]
    );

    // 2) 금액 -> 낙찰금액 vs 미낙찰금액(=총투찰-낙찰) (도넛)
    const bidWinAmountDonut = useMemo(
        () => [
            { name: "낙찰금액", value: totalWinAmount },
            { name: "미낙찰금액", value: lostAmount },
        ],
        [totalWinAmount, lostAmount]
    );

    // 3) 낙찰률 -> 낙찰률 vs 나머지 (도넛)
    const winRateDonut = useMemo(
        () => [
            { name: "낙찰률", value: winRate },
            { name: "기타", value: Math.max(0, 100 - winRate) },
        ],
        [winRate]
    );

    return (
        <>
            {/* 전역 로딩 */}
            <Backdrop open={loading} sx={{ zIndex: (t) => t.zIndex.drawer + 1, bgcolor: "rgba(255,255,255,0.85)" }}>
                <Stack spacing={1} alignItems="center">
                    <LoadingDots label="자료를 불러오고 있어요" />
                </Stack>
            </Backdrop>

            {/* ✅ 회색 배경 제거 */}
            <Box sx={ds.components.external.page}>
                <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
                    <Stack spacing={2.5}>
                        {/* 검색 */}
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                <SearchBar
                                    onSelect={(bn) => {
                                        window.location.href = `/search?type=${encodeURIComponent(
                                            "기업 검색"
                                        )}&bizNumber=${encodeURIComponent(bn)}`;
                                    }}
                                />
                            </CardContent>
                        </Card>

                        {/* 사업자 정보 1카드 */}
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                {company ? (
                                    <Stack spacing={0.5}>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Typography sx={ds.components.external.heroTitle}>
                                                {company.name}
                                            </Typography>
                                            {/* 기업 즐겨찾기 토글 — 사업자번호 키 */}
                                            <FavoriteStar
                                                checked={companyFav}
                                                onToggle={toggleCompanyFav}
                                                size="medium"
                                            />
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography variant="body2" color="text.secondary">
                                                사업자번호: {company.businessNumber}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label="기업"
                                                sx={{ ...ds.components.external.chip, height: 22 }}
                                            />
                                        </Stack>
                                    </Stack>
                                ) : (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <LoadingDots label="회사 정보를 불러오고 있어요" compact sx={{ py: 0 }} />
                                    </Stack>
                                )}
                            </CardContent>
                        </Card>

                        {/* 진행중 표시 */}
                        {progress && (
                            <Card sx={cardSx}>
                                <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                    <Stack spacing={0.75}>
                                        <Typography sx={{ fontWeight: 900, fontSize: 16 }}>
                                            낙찰 데이터 수집 중... (페이지 이탈 후 다른 작업하고 오셔도 괜찮습니다)
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {progress.fetched}/{progress.total} 건 완료 (
                                            {formatPercent(progress.progressPercent || 0)})
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                        )}

                        {/* 수집 실패 표시 — 멈춘 진행률 바 대신 «왜 실패했는지» 와 재시도 수단을 준다 */}
                        {taskError && (
                            <Card sx={cardSx}>
                                <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                    <Stack
                                        direction={{ xs: "column", md: "row" }}
                                        spacing={1.5}
                                        alignItems={{ xs: "flex-start", md: "center" }}
                                        justifyContent="space-between"
                                    >
                                        <Stack spacing={0.75}>
                                            <Typography sx={{ fontWeight: 900, fontSize: 16, color: "#dc2626" }}>
                                                낙찰 데이터 수집에 실패했습니다
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {taskError}
                                            </Typography>
                                        </Stack>
                                        <ActionButton
                                            variant="refresh"
                                            label="다시 시도"
                                            disabled={loading || !bizNumber}
                                            onClick={() => handleSearch(bizNumber)}
                                        />
                                    </Stack>
                                </CardContent>
                            </Card>
                        )}

                        {/* 요약 + 3 도넛 차트 */}
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                <Stack spacing={1.5}>
                                    <Typography sx={sectionTitleSx}>요약</Typography>

                                    {/* 숫자 */}
                                    {stats ? (
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" },
                                                gap: 1.5,
                                            }}
                                        >
                                            <MiniStat title="총 투찰" value={formatCount(totalBidCount)} />
                                            <MiniStat title="총 투찰금액" value={formatWon(totalBidAmount)} />
                                            <MiniStat title="낙찰" value={formatCount(totalWinCount)} accent={COLORS.winCount} />
                                            <MiniStat title="낙찰금액" value={formatWon(totalWinAmount)} accent={COLORS.winAmount} />
                                            <MiniStat title="낙찰률" value={formatPercent(winRate)} accent={COLORS.winRate} />
                                        </Box>
                                    ) : taskError ? null : !progress ? (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <LoadingDots label="통계 정보를 불러오고 있어요" compact sx={{ py: 0 }} />
                                        </Stack>
                                    ) : null}

                                    <Divider />

                                    {/* ✅ 3 Donuts */}
                                    {stats && (
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                                                gap: 2,
                                            }}
                                        >
                                            {/* 1) 투찰/낙찰(건수) */}
                                            <DonutCard
                                                title="투찰 / 낙찰 (건수)"
                                                subtitle="투찰 중 낙찰 건수 비중"
                                                mainLabel="낙찰 건수"
                                                mainValueText={formatCount(totalWinCount)}
                                                data={bidWinCountDonut}
                                                colors={[COLORS.winCount, COLORS.muted]}
                                                tooltipFormatter={(v, name) => [formatCount(v), name]}
                                            />

                                            {/* 2) 금액(투찰 중 낙찰액) */}
                                            <DonutCard
                                                title="총 투찰금액 중 낙찰금액"
                                                subtitle="총 투찰금액 대비 낙찰금액 비중"
                                                mainLabel="낙찰금액"
                                                mainValueText={formatWon(totalWinAmount)}
                                                data={bidWinAmountDonut}
                                                colors={[COLORS.winAmount, COLORS.muted]}
                                                tooltipFormatter={(v, name) => [formatWon(v), name]}
                                            />

                                            {/* 3) 낙찰률 */}
                                            <DonutCard
                                                title="낙찰률"
                                                subtitle="낙찰 비중(%)"
                                                mainLabel="낙찰률"
                                                mainValueText={formatPercent(winRate)}
                                                data={winRateDonut}
                                                colors={[COLORS.winRate, COLORS.muted]}
                                                tooltipFormatter={(v, name) => [formatPercent(v), name]}
                                            />
                                        </Box>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>

                        {/* ── 필터 적용분 집계 ────────────────────────────────────────────
                            위 도넛은 «업체 전량» 이라 필터를 걸어도 안 움직인다. 이 카드는 지금
                            화면에 남은 것만 센다 — 둘을 나란히 두면 «전체 대비 이 구간» 이 읽힌다. */}
                        {stats?.allResults && filterActive && (
                            <Card sx={cardSx}>
                                <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                    <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.5 }}>
                                        <Typography sx={{ ...ds.typography.sectionTitle, fontSize: 16 }}>
                                            필터 적용 집계
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            지금 목록에 보이는 {filteredSummary.count}건 기준 · 낙찰 판정은 위 카드와 같은 근거
                                        </Typography>
                                    </Stack>

                                    {/* 네 칸이 왼쪽에 뭉쳐 있고 오른쪽이 통째로 비어 있었다 —
                                        아래 월별 막대는 폭을 꽉 쓰는데 위만 짧아 카드가 어정쩡해 보인다.
                                        균등 그리드로 폭을 나눠 갖게 한다(좁은 화면에선 2칸). */}
                                    <Box sx={{
                                        display: "grid",
                                        gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
                                        gap: 1.5,
                                        mb: filteredSummary.months.length ? 2 : 0,
                                    }}>
                                        <MiniStat title="표시 건수" value={formatCount(filteredSummary.count)} />
                                        <MiniStat title="낙찰 건수" value={formatCount(filteredSummary.wonCount)} accent={COLORS.winCount} />
                                        <MiniStat title="낙찰금액 총액" value={formatWon(filteredSummary.wonAmount)} accent={ds.finance.income} />
                                        <MiniStat title={`${filteredSummary.thisYear}년 매출`} value={formatWon(filteredSummary.thisYearAmount)} accent={ds.finance.income} />
                                    </Box>

                                    {/* 월별 — 막대 길이는 «가장 큰 달» 대비 비율이다(절대 금액 아님). */}
                                    {filteredSummary.months.length > 0 && (() => {
                                        const max = Math.max(...filteredSummary.months.map((m) => m.amount), 1);
                                        return (
                                            <Box>
                                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: ds.colors.textMuted, mb: 0.75 }}>
                                                    월별 낙찰금액 (개찰일 기준)
                                                </Typography>
                                                <Stack spacing={0.5}>
                                                    {filteredSummary.months.map(({ month, amount, count, rows }) => (
                                                        // 호버로 «누를 수 있다» 를 알리고(초록→파랑), 누르면 그 달 내역을 편다.
                                                        // 호버만으로 모달을 띄우면 표를 지나가기만 해도 화면이 가려진다.
                                                        <Stack key={month} direction="row" alignItems="center" spacing={1}
                                                               onClick={() => setMonthDetail({ month, amount, count, rows })}
                                                               sx={{
                                                                   cursor: "pointer",
                                                                   borderRadius: 1,
                                                                   px: 0.5, mx: -0.5,
                                                                   "&:hover": { bgcolor: ds.colors.primaryBg },
                                                                   "&:hover .month-bar": { bgcolor: ds.colors.primary },
                                                               }}>
                                                            <Typography sx={{ fontSize: 11.5, width: 62, flexShrink: 0, color: ds.colors.textMuted, fontVariantNumeric: "tabular-nums" }}>
                                                                {month}
                                                            </Typography>
                                                            <Box sx={{ flex: 1, minWidth: 40, height: 8, bgcolor: ds.colors.borderLight, borderRadius: 4 }}>
                                                                <Box className="month-bar" sx={{
                                                                    width: `${Math.max(2, (amount / max) * 100)}%`,
                                                                    height: "100%",
                                                                    bgcolor: ds.finance.income,
                                                                    borderRadius: 4,
                                                                    transition: "background-color .15s",
                                                                }} />
                                                            </Box>
                                                            <Typography sx={{ fontSize: 11.5, width: 130, textAlign: "right", flexShrink: 0, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                                                                {formatWon(amount)}
                                                            </Typography>
                                                            {/* 막대는 금액 기준이라 «큰 건 하나» 와 «작은 건 여럿» 이 같아 보인다 — 건수로 가른다. */}
                                                            <Typography sx={{ fontSize: 11.5, width: 46, textAlign: "right", flexShrink: 0, color: ds.colors.textMuted, fontVariantNumeric: "tabular-nums" }}>
                                                                {count}건
                                                            </Typography>
                                                        </Stack>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        );
                                    })()}
                                </CardContent>
                            </Card>
                        )}

                        {/* 결과 목록 */}
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    spacing={1.5}
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    justifyContent="space-between"
                                    sx={{ mb: 1.5 }}
                                >
                                    <Box>
                                        <Typography sx={{ ...ds.typography.sectionTitle, fontSize: 18 }}>
                                            입찰 결과 목록
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {filterActive
                                                ? `필터 적용 ${displayResults.length}건 / 전체 ${stats?.allResults?.length ?? 0}건`
                                                : `총 ${displayResults.length}건 (낙찰 업체가 없는 건 낙찰 결과가 없는 것입니다)`}
                                        </Typography>
                                    </Box>

                                    <Stack direction="row" spacing={1} alignItems="center">
                                        {/* 가장 자주 쓰는 조건 하나는 모달을 열지 않고 바로 켤 수 있게 둔다.
                                            상태는 필터의 «낙찰 여부» 와 같은 값이라 둘이 항상 일치한다. */}
                                        <FormControlLabel
                                            sx={{ mr: 0.5 }}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={fWin === "WON"}
                                                    onChange={(e) => setFWin(e.target.checked ? "WON" : "ALL")}
                                                />
                                            }
                                            label={
                                                <Typography sx={{ fontSize: 12.5, whiteSpace: "nowrap" }}>
                                                    낙찰된 공고만 표시
                                                </Typography>
                                            }
                                        />
                                        {/* 필터·정렬은 평소엔 접어 둔다 — 열 줄짜리 입력칸이 목록 위에 상주하면
                                            정작 봐야 할 표가 아래로 밀린다. 캘린더 «유형 필터» 와 같은 패턴. */}
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<FilterListRoundedIcon sx={{ fontSize: 16 }} />}
                                            onClick={(e) => setFilterAnchor(e.currentTarget)}
                                            sx={{
                                                textTransform: "none", fontSize: 12, height: 36,
                                                flexShrink: 0, whiteSpace: "nowrap",
                                                borderColor: activeFilterCount ? "#2563eb" : "rgba(0,0,0,0.23)",
                                                color: activeFilterCount ? "#2563eb" : "#475569",
                                                bgcolor: activeFilterCount ? "#eff6ff" : "transparent",
                                            }}
                                        >
                                            필터 · 정렬{activeFilterCount ? ` ${activeFilterCount}` : ""}
                                        </Button>
                                        <ActionButton variant="download"
                                            onClick={handleDownload}
                                            disabled={!displayResults.length}
                                            label="엑셀 다운로드" />
                                    </Stack>
                                </Stack>

                                {/* ── 필터 · 정렬 ──────────────────────────────────────────────
                                    전부 화면에서 건다(서버 왕복 없음). 엑셀도 이 결과를 그대로 쓴다. */}
                                {/*
                                  팝오버가 아니라 <b>가운데 모달</b>이다.
                                  버튼 아래로 펼치면 패널이 표 위에 겹쳐 뜨는데, 조건을 만지는 동안
                                  정작 결과가 어떻게 변하는지 가려진다. 게다가 필터·정렬 두 블록이
                                  들어가 세로가 길어서 화면 아래로 잘리기까지 했다.
                                */}
                                <Dialog
                                    open={Boolean(filterAnchor)}
                                    onClose={() => setFilterAnchor(null)}
                                    maxWidth="md"
                                    fullWidth
                                >
                                  <DialogContent sx={{ p: 0 }}>
                                    <Box sx={{ p: 2.25 }}>
                                        <Stack direction="row" alignItems="flex-start"
                                               justifyContent="space-between" sx={{ mb: 1.75 }}>
                                            <Box>
                                                <Typography sx={{ fontSize: 13, fontWeight: 800, color: ds.colors.textPrimary }}>
                                                    필터 · 정렬
                                                </Typography>
                                                <Typography sx={{ fontSize: 11.5, color: ds.colors.textMuted, mt: 0.2 }}>
                                                    고르는 즉시 목록에 적용됩니다 · 엑셀도 이 결과 그대로 내려받습니다
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={1}>
                                                <ActionButton variant="refresh" onClick={resetFilters}
                                                    disabled={!filterActive} label="초기화" />
                                                <ActionButton variant="close" onClick={() => setFilterAnchor(null)} label="닫기" />
                                            </Stack>
                                        </Stack>

                                        {/* ── 필터 — 어떤 건을 남길지 ───────────────────────────── */}
                                        <PanelSection title="필터" desc="조건에 맞는 건만 남깁니다" badge={activeFilterCount} />

                                        <Stack spacing={1.25}>
                                            {/* 단독으로 서는 조건들 */}
                                            <Box sx={{
                                                display: "grid",
                                                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                                                columnGap: 1.25,
                                            }}>
                                                <FormField label="낙찰 여부">
                                                    <ComboBox options={WIN_OPTIONS} clearable={false}
                                                              value={fWin} onChange={setFWin} />
                                                </FormField>
                                                <FormField label="우리 순위">
                                                    <ComboBox options={RANK_OPTIONS} clearable={false}
                                                              value={fRank} onChange={setFRank} />
                                                </FormField>
                                                <FormField label="낙찰기업명">
                                                    <TextField size="small" fullWidth placeholder="업체명 일부"
                                                               value={fWinner}
                                                               onChange={(e) => setFWinner(e.target.value)} />
                                                </FormField>
                                            </Box>

                                            {/* 기간 — 세 칸이 한 조건이다 */}
                                            <FieldGroup title="기간"
                                                        hint="어느 날짜에 걸지 고르고, 그 날짜의 범위를 정합니다">
                                                <Box sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.3fr) auto minmax(0, 1.3fr)",
                                                    columnGap: 1, alignItems: "end",
                                                }}>
                                                    <FormField label="기준 날짜">
                                                        <ComboBox options={DATE_FIELD_OPTIONS} clearable={false}
                                                                  value={fDateField} onChange={setFDateField} />
                                                    </FormField>
                                                    <FormField label={`${labelOf(DATE_FIELD_OPTIONS, fDateField)} 시작`}>
                                                        <TextField type="date" size="small" fullWidth value={fFrom}
                                                                   onChange={(e) => setFFrom(e.target.value)} />
                                                    </FormField>
                                                    <Typography sx={{ fontSize: 12, color: ds.colors.textMuted, pb: 1.1 }}>~</Typography>
                                                    <FormField label={`${labelOf(DATE_FIELD_OPTIONS, fDateField)} 종료`}>
                                                        <TextField type="date" size="small" fullWidth value={fTo}
                                                                   onChange={(e) => setFTo(e.target.value)} />
                                                    </FormField>
                                                </Box>
                                            </FieldGroup>

                                            {/* 금액 — 둘 다 «최소~최대» 라 나란히 두면 서로를 설명해 준다 */}
                                            <FieldGroup title="금액" hint="비워 둔 쪽은 제한하지 않습니다 (단위: 원)">
                                                <Box sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                                    columnGap: 2,
                                                }}>
                                                    <FormField label="투찰금액 (우리가 넣은 금액)">
                                                        <RangeInputs min={fBidMin} max={fBidMax}
                                                                     onMin={setFBidMin} onMax={setFBidMax} />
                                                    </FormField>
                                                    <FormField label="낙찰금액 (실제 낙찰된 금액)">
                                                        <RangeInputs min={fWinMin} max={fWinMax}
                                                                     onMin={setFWinMin} onMax={setFWinMax} />
                                                    </FormField>
                                                </Box>
                                            </FieldGroup>
                                        </Stack>

                                        <Divider sx={{ my: 2 }} />

                                        {/* ── 정렬 — 어떤 순서로 볼지 ───────────────────────────── */}
                                        <PanelSection title="정렬"
                                                      desc="위 기준이 같을 때 아래 기준으로 넘어갑니다"
                                                      badge={sortRules.length > 1 ? sortRules.length : 0} />

                                        <Stack spacing={1}>
                                            {sortRules.map((rule, i) => {
                                                // 이미 쓴 기준은 아래 줄에서 감춘다 — 같은 기준을 두 번 걸면 뒤는 죽은 규칙이다.
                                                const usedElsewhere = new Set(
                                                    sortRules.filter((_, idx) => idx !== i).map((r) => r.key));
                                                return (
                                                    <Stack key={`${rule.key}-${i}`} direction="row" spacing={1} alignItems="center">
                                                        <Box sx={{
                                                            width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                                                            bgcolor: i === 0 ? ds.colors.primaryBg : ds.colors.borderLight,
                                                            color: i === 0 ? ds.colors.primary : ds.colors.textMuted,
                                                            fontSize: 11, fontWeight: 800,
                                                            display: "flex", alignItems: "center", justifyContent: "center",
                                                        }}>
                                                            {i + 1}
                                                        </Box>
                                                        <Box sx={{ width: 200 }}>
                                                            <ComboBox
                                                                clearable={false}
                                                                options={SORT_OPTIONS.filter((o) => !usedElsewhere.has(o.value))}
                                                                value={rule.key}
                                                                onChange={(v) => updateSortRule(i, { key: v })}
                                                            />
                                                        </Box>
                                                        <Box sx={{ width: 230 }}>
                                                            <ComboBox
                                                                clearable={false}
                                                                options={[
                                                                    { value: "desc", label: "내림차순 (큰 값 · 최근 먼저)" },
                                                                    { value: "asc",  label: "오름차순 (작은 값 · 오래된 먼저)" },
                                                                ]}
                                                                value={rule.asc ? "asc" : "desc"}
                                                                onChange={(v) => updateSortRule(i, { asc: v === "asc" })}
                                                            />
                                                        </Box>
                                                        <Button
                                                            size="small" onClick={() => removeSortRule(i)}
                                                            disabled={sortRules.length === 1}
                                                            sx={{
                                                                minWidth: 0, px: 1, fontSize: 12, textTransform: "none",
                                                                color: ds.colors.textMuted,
                                                            }}
                                                        >
                                                            삭제
                                                        </Button>
                                                    </Stack>
                                                );
                                            })}

                                            <Box>
                                                <Button
                                                    size="small"
                                                    startIcon={<SwapVertRoundedIcon sx={{ fontSize: 16 }} />}
                                                    onClick={addSortRule}
                                                    disabled={sortRules.length >= SORT_OPTIONS.length}
                                                    sx={{
                                                        textTransform: "none", fontSize: 12, fontWeight: 700,
                                                        color: ds.colors.primary,
                                                    }}
                                                >
                                                    정렬 기준 추가
                                                </Button>
                                                <Typography component="span"
                                                            sx={{ fontSize: 11.5, color: ds.colors.textMuted, ml: 1 }}>
                                                    지금: {sortSummary}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>
                                  </DialogContent>
                                </Dialog>

                                {/* ── 지금 적용된 조건 ────────────────────────────────────────
                                    패널은 닫으면 사라지지만 결과는 계속 좁혀져 있다. 그 간극이
                                    «왜 12건만 나오지» 의 원인이라 조건을 표 바로 위에 남겨 둔다. */}
                                <Stack direction="row" spacing={0.75} alignItems="center"
                                       sx={{ flexWrap: "wrap", rowGap: 0.75, mb: 1.25 }}>
                                    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: ds.colors.textMuted }}>
                                        적용 중
                                    </Typography>

                                    {filterChips.map((c) => (
                                        <Chip key={c.key} size="small" label={c.label} onDelete={c.clear}
                                              sx={{
                                                  height: 24, fontSize: 11.5, fontWeight: 600,
                                                  bgcolor: ds.colors.primaryBg, color: ds.colors.primaryText,
                                                  border: `1px solid ${ds.colors.primaryBorder}`,
                                                  "& .MuiChip-deleteIcon": { fontSize: 15, color: ds.colors.primarySoft },
                                              }} />
                                    ))}

                                    {!filterChips.length && (
                                        <Typography sx={{ fontSize: 11.5, color: ds.colors.textMuted }}>
                                            필터 없음 (전체)
                                        </Typography>
                                    )}

                                    {/* 정렬은 «꺼진» 상태가 없다 — 늘 무언가로 정렬돼 있으므로 항상 띄운다. */}
                                    <Box sx={{ flex: 1 }} />
                                    {/* 진입점은 «필터 · 정렬» 버튼 하나뿐이다 —
                                        여는 곳이 둘이면 «이 칩은 정렬만 여나?» 하고 헷갈린다.
                                        여기서는 현재 정렬을 «읽기 전용» 으로만 알려 준다. */}
                                    <Stack direction="row" alignItems="center" spacing={0.4}
                                           sx={{ color: ds.colors.textMuted }}>
                                        <SwapVertRoundedIcon sx={{ fontSize: 14 }} />
                                        <Typography sx={{ fontSize: 11.5, fontWeight: 600 }}>
                                            {sortSummary}
                                        </Typography>
                                    </Stack>
                                </Stack>

                                <Divider sx={{ mb: 1.5 }} />

                                {stats?.allResults ? (
                                    <Box sx={{
                                        width: "100%",
                                        overflowX: "auto",
                                        // 기본 패딩(좌우 16px)만으로 100px 넘게 먹는다 — 내용에 폭을 준다.
                                        // 한 칸에 두세 줄이 들어가므로 칸 경계가 없으면 어느 줄이 어느 칸인지 안 보인다.
                                        "& td, & th": {
                                            px: 1,
                                            py: 0.75,
                                            textAlign: "left",
                                            borderRight: `1px solid ${ds.colors.borderLight}`,
                                        },
                                        "& td:last-of-type, & th:last-of-type": { borderRight: 0 },
                                    }}>
                                        <ResizableTable size="small">
                                            <TableHead>
                                                {/* 헤더는 제목 한 줄만 — 기준별 링크를 늘어놓으면 제목이 안 보인다.
                                                    정렬은 제목 자체가 맡는다(한 칸의 대표 기준). */}
                                                <TableRow sx={{ ...ds.components.tableHead, "& th": { textAlign: "center" } }}>
                                                    <TableCell sx={{ width: 44, fontWeight: 900 }}>No</TableCell>
                                                    {sortCell("bidTitle", "공고명", { width: "42%", minWidth: 220 })}
                                                    {sortCell("bidNoticeDate", "일정", { width: 175 })}
                                                    {sortCell("bidPrice", "투찰", { width: 150 })}
                                                    {sortCell("winningPrice", "낙찰", { width: 190 })}
                                                </TableRow>
                                            </TableHead>

                                            <TableBody>
                                                {displayResults.map((r, i) => {
                                                    const selfWin = isSelfWin(r);
                                                    return (
                                                    <TableRow
                                                        key={r.id ?? i}
                                                        hover
                                                        sx={{
                                                            cursor: "pointer",
                                                            "&:hover": { bgcolor: ds.colors.primaryBg },
                                                        }}
                                                        onClick={() => setSelected(r)}
                                                    >
                                                        <TableCell sx={{
                                                            color: ds.colors.textMuted,
                                                            fontVariantNumeric: "tabular-nums",
                                                            textAlign: "center !important",
                                                        }}>
                                                            {i + 1}
                                                        </TableCell>

                                                        <TableCell>
                                                            <Typography variant="body2" sx={{
                                                                fontWeight: 700,
                                                                lineHeight: 1.35,
                                                                // 한 줄 자르기(noWrap)면 긴 공고명이 앞부분만 남아 서로 구분이 안 된다.
                                                                display: "-webkit-box",
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: "vertical",
                                                                overflow: "hidden",
                                                            }}>
                                                                {r.bidTitle}
                                                            </Typography>
                                                            {/* 공고번호-차수 — 같은 번호의 다른 차수가 따로 존재한다(재공고·취소공고). */}
                                                            <Typography sx={{
                                                                fontFamily: "monospace",
                                                                fontSize: 11,
                                                                color: ds.colors.textMuted,
                                                            }}>
                                                                {r.bidNoticeNumber || "-"}
                                                                {r.bidNoticeOrder ? `-${r.bidNoticeOrder}` : ""}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell sx={{ fontSize: 11.5, whiteSpace: "nowrap", lineHeight: 1.65 }}>
                                                            {[
                                                                ["게시", r.bidNoticeDate],
                                                                ["투찰", r.bidDate],
                                                                ["개찰", r.bidOpenDate],
                                                            ].map(([label, value]) => (
                                                                <Box key={label}>
                                                                    <Box component="span" sx={{ color: ds.colors.textMuted, mr: 0.5 }}>
                                                                        {label} :
                                                                    </Box>
                                                                    {formatDateTimeMinute(value, "-")}
                                                                </Box>
                                                            ))}
                                                        </TableCell>

                                                        <TableCell sx={{ fontSize: 11.5, whiteSpace: "nowrap", lineHeight: 1.65 }}>
                                                            <Box>
                                                                <Box component="span" sx={{ color: ds.colors.textMuted, mr: 0.5 }}>순위 :</Box>
                                                                <Box component="span" sx={{ fontWeight: 700 }}>
                                                                    {r.rank != null ? `${r.rank}위` : "-"}
                                                                </Box>
                                                            </Box>
                                                            <Box sx={{ fontVariantNumeric: "tabular-nums" }}>
                                                                <Box component="span" sx={{ color: ds.colors.textMuted, mr: 0.5 }}>금액 :</Box>
                                                                {r.bidPrice != null ? formatWon(r.bidPrice) : "-"}
                                                            </Box>
                                                        </TableCell>

                                                        {/* 검색한 업체가 낙찰자면 형광펜 — 목록에서 «우리가 딴 건» 이 한눈에 잡혀야 한다. */}
                                                        <TableCell sx={{ fontSize: 11.5, lineHeight: 1.65 }}>
                                                            <Box sx={isPlaceholderText(winnerLabel(r))
                                                                ? { color: "text.disabled" } : undefined}>
                                                                <Box component="span" sx={{ color: ds.colors.textMuted, mr: 0.5 }}>업체 :</Box>
                                                                {selfWin ? (
                                                                    <Box
                                                                        component="span"
                                                                        sx={{
                                                                            bgcolor: "#fde68a",   // 형광펜(노랑)
                                                                            color: "#713f12",
                                                                            fontWeight: 800,
                                                                            px: 0.6,
                                                                            py: 0.15,
                                                                            borderRadius: 0.5,
                                                                        }}
                                                                    >
                                                                        {winnerLabel(r)}
                                                                    </Box>
                                                                ) : winnerLabel(r)}
                                                            </Box>
                                                            <Box sx={{
                                                                fontVariantNumeric: "tabular-nums",
                                                                whiteSpace: "nowrap",
                                                                ...(isPlaceholderText(winningPriceLabel(r))
                                                                    ? { color: "text.disabled" } : {}),
                                                                ...(selfWin ? { fontWeight: 800 } : {}),
                                                            }}>
                                                                <Box component="span" sx={{ color: ds.colors.textMuted, mr: 0.5 }}>금액 :</Box>
                                                                {winningPriceLabel(r)}
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </ResizableTable>
                                    </Box>
                                ) : taskError ? null : !progress ? (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <LoadingDots label="입찰 결과 목록을 불러오고 있어요" compact sx={{ py: 0 }} />
                                    </Stack>
                                ) : (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <HourglassTopIcon fontSize="small" color="action" />
                                        <Typography variant="body2" color="text.secondary">
                                            작업 진행 중입니다...
                                        </Typography>
                                    </Stack>
                                )}
                            </CardContent>
                        </Card>

                        {/* 월별 낙찰 내역 — 막대를 누르면 그 달에 무엇을 따서 그 금액이 됐는지 편다. */}
                        <Dialog open={!!monthDetail} onClose={() => setMonthDetail(null)} maxWidth="md" fullWidth>
                            <DialogContent sx={{ p: 2.25 }}>
                                <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 1.5 }}>
                                    <Typography sx={{ ...ds.typography.sectionTitle, fontSize: 17 }}>
                                        {monthDetail?.month} 낙찰 내역
                                    </Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: ds.finance.income }}>
                                        {formatWon(monthDetail?.amount ?? 0)}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: ds.colors.textMuted }}>
                                        {monthDetail?.count ?? 0}건 · 개찰일 기준
                                    </Typography>
                                    <Box sx={{ flex: 1 }} />
                                    <ActionButton variant="close" onClick={() => setMonthDetail(null)} label="닫기" />
                                </Stack>

                                <Box sx={{
                                    width: "100%", overflowX: "auto", maxHeight: "60vh", overflowY: "auto",
                                    "& td, & th": { px: 1, py: 0.75 },
                                }}>
                                    <ResizableTable size="small">
                                        <TableHead>
                                            <TableRow sx={ds.components.tableHead}>
                                                <TableCell sx={{ width: 40 }}>No</TableCell>
                                                <TableCell sx={{ minWidth: 260 }}>공고명 / 공고번호</TableCell>
                                                <TableCell sx={{ width: 130 }}>개찰일시</TableCell>
                                                <TableCell sx={{ width: 70 }}>순위</TableCell>
                                                <TableCell sx={{ width: 130 }}>낙찰금액</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {/* 금액 큰 건부터 — «그 달을 만든 건» 이 위로 온다. */}
                                            {[...(monthDetail?.rows || [])]
                                                .sort((a, b) => (Number(b.winningPrice) || 0) - (Number(a.winningPrice) || 0))
                                                .map((r, i) => (
                                                <TableRow key={r.id ?? i} hover
                                                          sx={{ cursor: "pointer" }}
                                                          onClick={() => { setMonthDetail(null); setSelected(r); }}>
                                                    <TableCell sx={{ color: ds.colors.textMuted }}>{i + 1}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
                                                            {r.bidTitle}
                                                        </Typography>
                                                        <Typography sx={{ fontFamily: "monospace", fontSize: 11, color: ds.colors.textMuted }}>
                                                            {r.bidNoticeNumber}{r.bidNoticeOrder ? `-${r.bidNoticeOrder}` : ""}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                                                        {formatDateTimeMinute(r.bidOpenDate, "-")}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>
                                                        {r.rank != null ? `${r.rank}위` : "-"}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: 12, fontWeight: 800, whiteSpace: "nowrap",
                                                                     fontVariantNumeric: "tabular-nums" }}>
                                                        {winningPriceLabel(r)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </ResizableTable>
                                </Box>
                            </DialogContent>
                        </Dialog>

                        {/* 상세 모달 */}
                        <BidDetailModal
                            bidNumber={selected?.bidNoticeNumber}
                            bidNoticeOrder={selected?.bidNoticeOrder}
                            isOpen={!!selected}
                            onClose={() => setSelected(null)}
                        />
                    </Stack>
                </Box>
            </Box>
        </>
    );
};

export default CompanySearchPage;
